import { LitElement, html, css, ifDefined, styleMap, paths, unsafeHTML, utils } from '@hh/global.js'
import { renderScene, clearScene } from '@hh/bottle-engraving.js'

class HHBottleEngravingPreview extends LitElement {
  static properties = {
    type: {
      type: String,
    },
    currentVariant: {
      type: Object,
    },
    currentVariantMetafields: {
      type: Object,
    },
    fontColor: {
      type: String,
      attribute: 'font-color',
    },
    fontStyle: {
      type: String,
    },
    textLine1: {
      type: String,
    },
    textLine2: {
      type: String,
    },
    initials: {
      type: String,
    },
    embossingInitials: {
      type: String,
    },
    medallionFontStyle: {
      type: String,
    },
    embossingFontStyle: {
      type: String,
    },
    compact: {
      type: Boolean,
      reflect: true,
    },
    fullScreen: {
      type: Boolean,
      default: false,
      reflect: true,
      attribute: 'full-screen',
    },
    medallionEngravingImage: {
      type: String,
    },
    medallionEngravingImageAlt: {
      type: String,
    },
    threeJsScene: {
      type: Object,
    },
    customizerShadowWrapper: {
      type: Object,
    },
    previewShadowWrapper: {
      type: Object,
    },
    assetsUrl: {
      type: String,
    },
    desktopVerticalTextPosition: {
      type: String,
      attribute: 'desktop-vertical-text-position',
    },
    desktopHorizontalTextPosition: {
      type: String,
      attribute: 'desktop-horizontal-text-position',
    },
    mobileVerticalTextPosition: {
      type: String,
      attribute: 'mobile-vertical-text-position',
    },
    mobileHorizontalTextPosition: {
      type: String,
      attribute: 'mobile-horizontal-text-position',
    },
    desktopEngravingMaxWidth: {
      type: String,
      attribute: 'desktop-engraving-max-width',
    },
    mobileEngravingMaxWidth: {
      type: String,
      attribute: 'mobile-engraving-max-width',
    },
  }

  constructor() {
    super()
    this.textLine1 = ''
    this.textLine2 = ''
    this.initials = ''
    this.textLineStyleMap = {}
    this.embossingInitials = ''
    this.isMobile = matchMedia('(max-width: 1024px)').matches

    this.fontStyles = {
      simple: 'calibri',
      block: 'garamond',
      script: 'carattere',
    }
    this.assetsUrl = this.dataset?.shopAssetsUrl ?? ''

  }

  get personalizeComponent() {
    return document.querySelector('hh-personalize') ?? null
  }

  get bottlePersonalizationComponent() {
    return (
      document.querySelector(
        'hh-bottle-engraving-personalization[type="bottle"]',
      ) ?? null
    )
  }

  get medallionPersonalizationComponent() {
    return (
      document.querySelector(
        'hh-bottle-engraving-personalization[type="medallion"]',
      ) ?? null
    )
  }

  engravingFontColor() {
    this.textLineStyleMap.color = this.variantPersonalizationMetafields.fontColor
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener(
      'drawer:open',
      this.onPersonalizeDrawerOpen.bind(this),
    )

    window.addEventListener(
      'variant:change',
      this.onVariantChange.bind(this),
    )

    window.addEventListener(
      'personalize:update:style',
      this.onStyleUpdate.bind(this),
    )

    window.addEventListener(
      'personalize:update:color',
      this.onColorUpdate.bind(this),
    )

    window.addEventListener(
      'personalize:update:line',
      this.onLineUpdate.bind(this),
    )
    window.addEventListener(
      'double-drawer:open',
      this.renderThreeJsScene.bind(this),
    )
    window.addEventListener(
      'customizer-drawer:close',
      this.clearThreeJsScene.bind(this),
    )

    window.addEventListener('resize', this.updateFontSize.bind(this))


    window.addEventListener('resize', () => {
      this.previewShadowWrapper = document.getElementById(
        screen.width >= 1024
          ? 'hh-bottle-customizer-preview-desktop'
          : 'hh-bottle-customizer-preview-mobile',
      )
    })

    this.previewShadowWrapper = document.getElementById(
      screen.width >= 1024
        ? 'hh-bottle-customizer-preview-desktop'
        : 'hh-bottle-customizer-preview-mobile',
    )
    this.customizerShadowWrapper = document.getElementById(
      'hh-bottle-customizer',
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('drawer:open', this.onPersonalizeDrawerOpen)
    window.removeEventListener('variant:change', this.onVariantChange)
    window.removeEventListener('personalize:update:style', this.onStyleUpdate)
    window.removeEventListener('personalize:update:line', this.onLineUpdate)
    window.removeEventListener('resize', this.updateFontSize.bind(this))
  }

  firstUpdated() {
    this.updateFontSize()
  }

  updated(changedProperties) {
    if (changedProperties.has('fontStyle')) {
      this.updateFontVariables(this.fontStyle)
    }
    if (changedProperties.has('medallionFontStyle')) {
      this.updateMedallionFontVariables(this.medallionFontStyle)
    }
    if (changedProperties.has('embossingFontStyle')) {
      this.updateEmbossingFontVariables(this.embossingFontStyle)
    }
  }

  get personalizationMetafields() {
    const metafieldsStriptTag = document.getElementById(
      'personalizationMetafields',
    )
    const json = metafieldsStriptTag
      ? JSON.parse(metafieldsStriptTag.textContent)
      : null
    return json
  }

  get variantPersonalizationMetafields() {
    return this.personalizationMetafields.find(
      (variant) => variant.id === this.currentVariant.id,
    )
  }

  get medallionEngravingProduct() {
    const medallionScriptTag = document.getElementById(
      'medallionEngravingProduct',
    )
    if (!medallionScriptTag) return null
    return JSON.parse(medallionScriptTag.innerHTML)
  }

  onPersonalizeDrawerOpen(e) {
    const { target } = e
    if (!target?.classList.contains('js-personalize-drawer')) return
    this.currentVariant = this.personalizeComponent?.currentVariant
    this.currentVariantMetafields = this.variantPersonalizationMetafields


    if (this.type === 'bottle') {
      this.engravingFontColor()
      this.maxEngravingWidth()
      this.fontStyle = this.bottlePersonalizationComponent.defaultStyle
    } else if (this.type === 'embossing') {
      this.engravingFontColor()
    } else if (this.type === 'medallion') {
      this.medallionFontStyle =
        this.medallionPersonalizationComponent.defaultStyle
      this.medallionEngravingImage = this.medallionEngravingProduct.find(
        (variant) =>
          variant.title === this.medallionPersonalizationComponent.defaultColor,
      )?.image
      this.medallionEngravingImageAlt = this.medallionEngravingProduct.find(
        (variant) =>
          variant.title === this.medallionPersonalizationComponent.defaultColor,
      )?.alt
    }

    this.textPositionStyleMap()
    this.updateFontSize()
  }

  onVariantChange(e) {
    const { detail } = e
    this.currentVariant = detail.variant
    this.currentVariantMetafields = this.personalizationMetafields.find(
      (variant) => variant.id === detail.variant.id
    )
    
    if (this.type === 'bottle') {
      this.engravingFontColor()
      this.maxEngravingWidth()
    } else if (this.type === 'embossing') {
      this.engravingFontColor()
    }
    
    this.textPositionStyleMap()
    this.updateFontSize()
  }

  onStyleUpdate(e) {
    const { detail } = e
    const { style, type } = detail
    if (type === 'bottle') {
      this.fontStyle = style
    } else if (type === 'medallion') {
      this.medallionFontStyle = style
    } else if (type === 'embossing') {
      this.embossingFontStyle = style
    }
  }

  onColorUpdate(e) {
    const { detail } = e
    const { color } = detail

    const matchingMedallionVariant = this.medallionEngravingProduct.find(
      (variant) => variant.title === color,
    )
    this.medallionEngravingImage = matchingMedallionVariant.image
  }

  onLineUpdate(e) {
    const { detail } = e
    const { line, value, type } = detail
    if (type === 'bottle') {
      this[`textLine${line}`] = value

      this.adjustFontSize(line)
    } else if (type === 'medallion') {
      this.initials = value
    } else if (type === 'embossing') {
      this.embossingInitials = value

      this.textPositionStyleMap()
    }
  }

  updateFontVariables(fontStyle) {
    HH.setCSSVariable(
      '--hh-personalize-font-family',
      this.fontStyles[fontStyle],
    )

    this.updateFontSize()
  }

  updateFontSize() {
    let size = (this.getBoundingClientRect().width / 100) * 5
    let sizeMobile = size
    if (this.type === 'bottle') {
      size = this.currentVariantMetafields?.bottleEngravingFontSize || size
      sizeMobile = size
      this.style.setProperty('--hh-personalize-font-size', !!size ? `${size}px` : '')
      this.style.setProperty('--hh-personalize-font-size-mobile', !!sizeMobile ? `${sizeMobile}px` : '')
    } else if (this.type === 'medallion') {
      size = this.currentVariantMetafields?.medallionEngravingFontSize || size
      sizeMobile = size
      this.style.setProperty('--hh-personalize-font-size', !!size ? `${size}px` : '')
      this.style.setProperty('--hh-personalize-font-size-mobile', !!sizeMobile ? `${sizeMobile}px` : '')
    } else if (this.type === 'embossing') {
      size = this.currentVariantMetafields?.leatherEmbossingFontSize || size
      sizeMobile = this.currentVariantMetafields?.leatherEmbossingFontSizeMobile || size
      this.style.setProperty('--hh-personalize-font-size', !!size ? `calc(calc(95vw - 579px - 5.0000rem) / ${size})` : '')
      this.style.setProperty('--hh-personalize-font-size-mobile', !!sizeMobile ? `calc(90vw / ${sizeMobile})` : '')
    }
  }

  updateMedallionFontVariables(medallionFontStyle) {
    HH.setCSSVariable(
      '--hh-personalize-medallion-font-family',
      this.fontStyles[medallionFontStyle],
    )
  }

  updateEmbossingFontVariables(embossingFontStyle) {
    HH.setCSSVariable(
      '--hh-personalize-embossing-font-family',
      this.fontStyles[embossingFontStyle],
    )
  }

  maxEngravingWidth() {
    this.isMobile
      ? this.textLineStyleMap.maxWidth = this.mobileEngravingMaxWidth
      : this.textLineStyleMap.maxWidth = this.desktopEngravingMaxWidth
  }

  textPositionStyleMap() {
    let positionAttributes = {}
    let mobileVerticalTextPosition = this.mobileVerticalTextPosition
    let mobileHorizontalTextPosition = this.mobileHorizontalTextPosition
    let desktopVerticalTextPosition = this.desktopVerticalTextPosition
    let desktopHorizontalTextPosition = this.desktopHorizontalTextPosition

    if (this.type === 'bottle') {
      mobileVerticalTextPosition = this.currentVariantMetafields?.bottleEngravingVerticalPos || mobileVerticalTextPosition
      mobileHorizontalTextPosition = this.currentVariantMetafields?.bottleEngravingHorizontalPos || mobileHorizontalTextPosition
      desktopVerticalTextPosition = this.currentVariantMetafields?.bottleEngravingVerticalPos || desktopVerticalTextPosition
      desktopHorizontalTextPosition = this.currentVariantMetafields?.bottleEngravingHorizontalPos || desktopHorizontalTextPosition
    } else if (this.type === 'medallion') {
      mobileVerticalTextPosition = this.currentVariantMetafields?.medallionEngravingVerticalPos || mobileVerticalTextPosition
      mobileHorizontalTextPosition = this.currentVariantMetafields?.medallionEngravingHorizontalPos || mobileHorizontalTextPosition
      desktopVerticalTextPosition = this.currentVariantMetafields?.medallionEngravingVerticalPos || desktopVerticalTextPosition
      desktopHorizontalTextPosition = this.currentVariantMetafields?.medallionEngravingHorizontalPos || desktopHorizontalTextPosition
    } else if (this.type === 'embossing') {
      if (this.embossingInitials.length > this.currentVariantMetafields?.leatherEmbossingMaxChars) {
        mobileVerticalTextPosition = this.currentVariantMetafields?.verticalLeatherEmbossingVerticalPos || mobileVerticalTextPosition
        mobileHorizontalTextPosition = this.currentVariantMetafields?.verticalLeatherEmbossingHorizontalPos || mobileHorizontalTextPosition
        desktopVerticalTextPosition = this.currentVariantMetafields?.verticalLeatherEmbossingVerticalPos || desktopVerticalTextPosition
        desktopHorizontalTextPosition = this.currentVariantMetafields?.verticalLeatherEmbossingHorizontalPos || desktopHorizontalTextPosition
        this.textLineStyleMap.writingMode = 'vertical-lr'
      } else {
        mobileVerticalTextPosition = this.currentVariantMetafields?.leatherEmbossingVerticalPos || mobileVerticalTextPosition
        mobileHorizontalTextPosition = this.currentVariantMetafields?.leatherEmbossingHorizontalPos || mobileHorizontalTextPosition
        desktopVerticalTextPosition = this.currentVariantMetafields?.leatherEmbossingVerticalPos || desktopVerticalTextPosition
        desktopHorizontalTextPosition = this.currentVariantMetafields?.leatherEmbossingHorizontalPos || desktopHorizontalTextPosition
        this.textLineStyleMap.writingMode = ''
      }
    }

    if (this.isMobile) {
      this.textLineStyleMap.top = !!mobileVerticalTextPosition ? `${mobileVerticalTextPosition}%` : ''
      this.textLineStyleMap.left = !!mobileHorizontalTextPosition ? `${mobileHorizontalTextPosition}%` : ''
    } else {
      this.textLineStyleMap.top = !!desktopVerticalTextPosition ? `${desktopVerticalTextPosition}%` : ''
      this.textLineStyleMap.left = !!desktopHorizontalTextPosition ? `${desktopHorizontalTextPosition}%` : ''
    }
  }

  get bottleEngravingImage() {
    return ifDefined(
      this.compact
        ? this.currentVariantMetafields?.bottleEngravingImageMobile?.src || this.currentVariantMetafields?.bottleEngravingImageMobile
        : this.currentVariantMetafields?.bottleEngravingImage?.src || this.currentVariantMetafields?.bottleEngravingImage,
    )
  }

  get leatherEmbossingImage() {
    return ifDefined(
      this.compact
        ? this.currentVariantMetafields?.leatherEmbossingImageMobile?.src || this.currentVariantMetafields?.leatherEmbossingImageMobile
        : this.currentVariantMetafields?.leatherEmbossingImage?.src || this.currentVariantMetafields?.leatherEmbossingImage,
    )
  }

  get previewImage() {
    switch (this.type) {
      case 'bottle':
        return this.bottleEngravingImage
      case 'medallion':
        return this.medallionEngravingImage
      case 'embossing':
        return this.leatherEmbossingImage
      default:
        return null
    }
  }

  get previewImageAlt() {
    switch (this.type) {
      case 'medallion':
        return this.medallionEngravingImageAlt
      default:
        return null
    }
  }

  adjustFontSize(index) {
    if (!this.shadowRoot.querySelector('.overlay-text')) return

    this.shadowRoot.querySelectorAll(`.line[data-line="${index}"]`).forEach(line => {
      const { width } = line.getBoundingClientRect()
      if (width > 0) this.validateTextOverflow(line)
    })
  }

  validateTextOverflow(line) {
    const defaultFontSize = (this.getBoundingClientRect().width / 100) * 5
    const imageWrapperWidth = this.shadowRoot.querySelector('.image-wrapper').getBoundingClientRect().width
    const textContainerWidth = this.isMobile ? imageWrapperWidth * 0.75 : imageWrapperWidth * 0.5

    let overflow = false
    let i = 5 // Initial font size
    const step = 0.5

    while (!overflow) {
      line.style.fontSize = `${i}px`

      const { width } = line.getBoundingClientRect()
      overflow = width > textContainerWidth

      if (!overflow) i += step
    }

    i -= step

    line.style.fontSize = `${Math.min(i, defaultFontSize)}px`
  }

  toggleFullScreen() {
    this.fullScreen = !this.fullScreen
    this.compact = !this.compact

    this.clearThreeJsScene()

    if (this.fullScreen) {
      document.querySelector('#bottle-engraving-drawer').style.setProperty('--hh-max-width', '100%')
    } else {
      document.querySelector('#bottle-engraving-drawer').style.setProperty('--hh-max-width', '95%')
    }

    setTimeout(() => {
      this.renderThreeJsScene({ fullscreen: this.fullScreen })
    }, 300)
  }

  overlayTextTemplate() {
    if (this.type === 'bottle') {
      return html`
        <div class='overlay-text' style='${styleMap(this.textLineStyleMap)}'>
          <div>
            <p class='line' data-line='1'>${this.textLine1}</p>
          </div>
          <div>
            <p class='line' data-line='2'>${this.textLine2}</p>
          </div>
        </div>
      `
    } else if (this.type === 'medallion') {
      return html`
        <div
          class='overlay-text medallion-text-overlay'
          style=${styleMap(this.textLineStyleMap)}
        >
          <p class='line'>${this.initials}</p>
        </div>
      `
    } else if (this.type === 'embossing') {
      return html`
        <div
          class='overlay-text embossing-text-overlay'
          style=${styleMap(this.textLineStyleMap)}
        >
          <p class='line'>${this.embossingInitials}</p>
        </div>
      `
    }
  }

  renderDefaultEngravingPreview() {
    return html`
      <div class='container'>
        <div class='image-wrapper'>
          <img class='image' src='${this.previewImage}' alt='${this.previewImageAlt}' />
          ${this.overlayTextTemplate()}
        </div>
      </div>
    `
  }

  renderThreeJSEngravingPreview() {
    return html`
      <div id="three-js-bottle-engraving">
        <div id="preloader">
          <p>Loading...</p>
          <div id="progress-bar">
            <div id="progress"></div>
          </div>
        </div>
        <div id="app"></div>
        <div id="custom-cursor" class="hidden">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 15 13"
          >
            <path
              stroke="currentColor"
              stroke-width="1.314"
              d="M14.414 6.728H1.934m4.598 5.582L.95 6.728l5.583-5.583"
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 15 13"
          >
            <path
              stroke="currentColor"
              stroke-width="1.314"
              d="M.414 6.728h12.48M8.296 1.145l5.583 5.582-5.583 5.583"
            />
          </svg>
        </div>

        <button
          class="personalize__fullscreen"
          type="button"
          @click="${this.toggleFullScreen}"
        >
          ${unsafeHTML(paths.fullscreen)}
        </button>
      </div>
    `
  }

  get sceneObj() {
    return {
      customizerShadowWrapper: this.customizerShadowWrapper,
      previewShadowWrapper: this.previewShadowWrapper,
      engravingEnabled: this.currentVariantMetafields?.engravingEnabled,
      modelUrl: this.currentVariantMetafields?.modelFile,
      assetsUrl: this.assetsUrl,
      fontColor: this.currentVariantMetafields?.fontColor,
      colourTexture: this.currentVariantMetafields?.colourTexture,
      mraTexture: this.currentVariantMetafields?.mraTexture,
      normalTexture: this.currentVariantMetafields?.normalTexture,
      envMapParam: this.currentVariantMetafields?.envMap,
      fullscreen: this.fullScreen,
    }
  }

  renderThreeJsScene() {
    this.threeJsScene =
      this.type === 'bottle' &&
      this.sceneObj?.engravingEnabled &&
      this.id === this.previewShadowWrapper.id &&
      renderScene(this.sceneObj)
  }

  clearThreeJsScene() {
    if (this.threeJsScene) {
      clearScene({
        scene: this.threeJsScene,
        previewShadowWrapper: this.previewShadowWrapper,
      })
    }
  }

  render() {
    if (!this.currentVariant) return
    const engravingEnabled = this.sceneObj?.engravingEnabled

    return engravingEnabled && this.type === 'bottle'
      ? this.renderThreeJSEngravingPreview()
      : this.renderDefaultEngravingPreview()
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    @media (min-width: 1024px) {
      :host([compact]) {
        display: none;
      }
    }

    .container {
      padding: 2.5000rem 2.2500rem;
    }

    :host([compact]) .container {
      padding: 0;
      margin: 0 0 1.6250rem;
    }

    .image-wrapper {
      position: relative;
    }

    .image {
      width: 100%;
      height: 100%;
      display: block;
    }

    .overlay-text {
      position: absolute;
      text-align: center;
      top: 27%;
      left: 50%;
      color: white;
      transform: translate(-50%, -50%);
      font-family: var(--hh-personalize-font-family);
      display: flex;
      flex-direction: column;
    }

    .line {
      margin: 0;
      line-height: 1;
      font-size: var(--hh-personalize-font-size-mobile, max(24px, 0.6250rem + 2.5vw));
      white-space: nowrap;
      display: inline-block;
    }

    .line:empty {
      display: none;
      line-height: 1.1;
      font-size: var(--hh-personalize-font-size-mobile, max(24px, 0.6250rem + 2.5vw));
    }

    @media (min-width: 600px) {
      .line {
        font-size: var(--hh-personalize-font-size, max(24px, 0.6250rem + 2.5vw));
      }
    }

    .medallion-text-overlay {
      top: 46%;
      left: 53%;
      transform: translate(-50%, -50%);
      font-family: var(--hh-personalize-medallion-font-family);
    }

    .medallion-text-overlay .line {
      font-size: var(--hh-personalize-font-size, 3.1250rem);
    }

    .embossing-text-overlay {
      top: 83%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: var(--hh-color-embossing);
      font-family: var(--hh-personalize-embossing-font-family, 'Copperplate');
    }

    .embossing-text-overlay .line {
      text-transform: uppercase;
      font-size: var(--hh-personalize-font-size-mobile);
      font-weight: 300;
    }

    @media (min-width: 1024px) {
      .embossing-text-overlay .line {
        font-size: var(--hh-personalize-font-size);
      }
    }

    #app {
      width: 100%;
      height: 50dvh;
      min-width: 100%;
      min-height: 50dvh;
      cursor: grab;
    }


    @media (min-width: 600px) {
       #app {
        width: 100%;
        height: 100%;
        min-width: 100%;
        min-height: 100dvh;
        cursor: grab;
      }
    }

    #preloader {
      --progress: 0;
      font-family: var(--hh-personalize-font-family);
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 9;
      background-color: black;
      transition: opacity 0.3s ease-out;
      pointer-events: none;
      color: white;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    #preloader.hidden {
      opacity: 0;
    }

    #progress-bar {
      position: relative;
      border: 1px solid white;
      border-radius: 1100vw;
      min-width: 200px;
      height: 10px;
    }

    #progress {
      height: 100%;
      background-color: white;
      width: 0%;
      transition: width 0.3s ease-out;
    }

    #custom-cursor {
      position: absolute;
      top: 0;
      left: 0;
      background-color: white;
      color: #191919;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      width: 120px;
      padding: 18px 30px;
      border-radius: 100vw;
      pointer-events: none;
      transition: opacity 0.3s ease-out, width 0.1s ease-out;
      opacity: 1;
      transform: translate(-50%, 30px);
    }

    #custom-cursor.clicked {
      width: 110px;
    }

    #custom-cursor.hidden {
      opacity: 0;
    }

    #custom-cursor svg {
      width: 12px;
    }

    #three-js-bottle-engraving {
      position: relative;
    }

    .personalize__fullscreen {
      width: 25px;
      height: 25px;
      position: absolute;
      right: 10px;
      bottom: 10px;
      background: transparent;
      border: none;
    }

    :host([full-screen]) #three-js-bottle-engraving {
      position: fixed;
      left: 0;
      top: 0;
      width: 100dvw;
      height: 100dvh;
      z-index: 10;
      overflow: hidden;
    }

    :host([full-screen]) #app {
      width: 100%;
      height: 100%;
    }
  `
}

customElements.define('hh-bottle-engraving-preview', HHBottleEngravingPreview)
